/**
 * Permisos directos usuario–reporte.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { studio, RoleBadge, SearchField, FilterDock, FilterPill, FilterMeta } from './studioUi';
import ReportAccessPicker from './ReportAccessPicker';

const userEmail = (user) => user?.correo || user?.email || '';

const PermissionManagement = ({
    users,
    biblioteca,
    permisosActuales,
    setPermisosActuales,
    selectedUserForPermissions,
    setSelectedUserForPermissions,
    styles,
    userPermissions,
    fetchWithAuth,
    API_URL,
    embedded = false,
    cargarTodo,
}) => {
    const [filtroEmpresa, setFiltroEmpresa] = useState('');
    const [filtroArea, setFiltroArea] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [hoveredRow, setHoveredRow] = useState(null);
    const [busy, setBusy] = useState(false);

    const countsByUser = useMemo(() => {
        const counts = {};
        (permisosActuales || []).forEach((perm) => {
            if (!perm.userId) return;
            counts[perm.userId] = (counts[perm.userId] || 0) + 1;
        });
        return counts;
    }, [permisosActuales]);

    const findPermission = useCallback((report) => {
        if (!selectedUserForPermissions) return null;
        const uid = selectedUserForPermissions.uid;
        return (permisosActuales || []).find((perm) => (
            perm.userId === uid && (perm.reporteId === report.id || (report.dashboardUrl && perm.dashboardUrl === report.dashboardUrl))
        )) || null;
    }, [permisosActuales, selectedUserForPermissions]);

    const handleToggle = useCallback(async (report, grant) => {
        if (!selectedUserForPermissions) return false;
        const userId = selectedUserForPermissions.uid;
        if (grant) {
            const payload = {
                userId,
                reporteId: report.id,
                cliente: report.cliente,
                clientId: report.clientId,
                proyecto: report.proyecto,
                projectId: report.projectId,
                reporteNombre: report.reporteNombre,
                dashboardUrl: report.dashboardUrl,
                accessType: 'view',
            };
            const res = await fetchWithAuth(`${API_URL}/admin/assign-report`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                alert(data.error || 'Error al asignar permiso.');
                return false;
            }
            if (!data.id) {
                if (cargarTodo) cargarTodo();
                return true;
            }
            setPermisosActuales((prev) => [
                ...prev.filter((perm) => !(perm.userId === userId && (perm.reporteId === report.id || (report.dashboardUrl && perm.dashboardUrl === report.dashboardUrl)))),
                { id: data.id, ...payload },
            ]);
            return true;
        }

        const permission = findPermission(report);
        if (!permission?.id) return false;
        const res = await fetchWithAuth(`${API_URL}/admin/delete-permission/${permission.id}`, { method: 'DELETE' });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            alert(data.error || 'Error al revocar el acceso.');
            return false;
        }
        setPermisosActuales((prev) => prev.filter((perm) => perm.id !== permission.id));
        return true;
    }, [API_URL, fetchWithAuth, findPermission, selectedUserForPermissions, setPermisosActuales, cargarTodo]);

    const handleBulkToggle = useCallback(async (reports, grant) => {
        setBusy(true);
        try {
            for (const report of reports) {
                await handleToggle(report, grant);
            }
        } finally {
            setBusy(false);
        }
    }, [handleToggle]);

    const handleAccessTypeChange = useCallback(async (permissionId, newAccessType) => {
        const res = await fetchWithAuth(`${API_URL}/admin/permissions/${permissionId}/access-type`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accessType: newAccessType }),
        });
        if (!res.ok) {
            alert('Error al actualizar el permiso.');
            return false;
        }
        setPermisosActuales((prev) => prev.map((perm) => (
            perm.id === permissionId ? { ...perm, accessType: newAccessType } : perm
        )));
        return true;
    }, [API_URL, fetchWithAuth, setPermisosActuales]);

    if (selectedUserForPermissions) {
        const granted = (permisosActuales || []).filter((perm) => perm.userId === selectedUserForPermissions.uid).length;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={studio.card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            <img
                                src={selectedUserForPermissions.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUserForPermissions.nombre)}&background=5E35B1&color=fff&size=48`}
                                alt=""
                                style={{ ...studio.avatar, width: 48, height: 48 }}
                            />
                            <div>
                                <p style={studio.kicker}>Acceso directo</p>
                                <h3 style={{ ...studio.title, fontSize: '1.35rem' }}>{selectedUserForPermissions.nombre}</h3>
                                <p style={{ ...studio.subtitle, marginTop: 4, maxWidth: 'none' }}>
                                    {userEmail(selectedUserForPermissions)} · {granted} {granted === 1 ? 'reporte' : 'reportes'}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSelectedUserForPermissions(null)}
                            style={studio.ghostBtn}
                        >
                            Volver a personas
                        </button>
                    </div>
                </div>
                <div style={studio.card}>
                    <h4 style={studio.formTitle}>Reportes disponibles</h4>
                    <p style={{ ...studio.hint, marginBottom: 16 }}>
                        Activa el acceso y elige si la persona solo puede ver el reporte o también editarlo.
                    </p>
                    <ReportAccessPicker
                        reports={biblioteca}
                        getPermission={findPermission}
                        onToggle={handleToggle}
                        onBulkToggle={handleBulkToggle}
                        onAccessTypeChange={handleAccessTypeChange}
                        busy={busy}
                    />
                </div>
            </div>
        );
    }

    const availableUsers = (users || []).filter((user) => (
        userPermissions.users_view_admin_pm ? true : (user.rol !== 'admin' && user.rol !== 'pm')
    ));
    const empresasUnicas = [...new Set(availableUsers.map((user) => user.empresa || 'Sin Empresa'))].filter(Boolean).sort();
    const areasUnicas = [...new Set(availableUsers.map((user) => user.area))].filter(Boolean).sort();

    const usuariosFiltrados = availableUsers.filter((user) => {
        if (filtroEmpresa && user.empresa !== filtroEmpresa) return false;
        if (filtroArea && user.area !== filtroArea) return false;
        const term = searchTerm.toLowerCase();
        if (!term) return true;
        return (user.nombre || '').toLowerCase().includes(term) || userEmail(user).toLowerCase().includes(term);
    });

    const usuariosPorEmpresa = usuariosFiltrados.reduce((acc, user) => {
        const empresa = user.empresa || 'Sin Empresa';
        if (!acc[empresa]) acc[empresa] = [];
        acc[empresa].push(user);
        return acc;
    }, {});
    const empresasOrdenadas = Object.keys(usuariosPorEmpresa).sort();

    return (
        <div>
            <div style={studio.card}>
                {!embedded && <h3 style={{ margin: '0 0 16px' }}>Gestión de accesos</h3>}
                <FilterDock>
                    <SearchField value={searchTerm} onChange={setSearchTerm} placeholder="Buscar por nombre o email…" />
                    <div style={studio.filterPills}>
                        <FilterPill value={filtroEmpresa} onChange={setFiltroEmpresa} emptyLabel="Empresa">
                            {empresasUnicas.map((empresa) => <option key={empresa} value={empresa}>{empresa}</option>)}
                        </FilterPill>
                        <FilterPill value={filtroArea} onChange={setFiltroArea} emptyLabel="Área">
                            {areasUnicas.map((area) => <option key={area} value={area}>{area}</option>)}
                        </FilterPill>
                    </div>
                    <FilterMeta
                        summary={`${usuariosFiltrados.length} ${usuariosFiltrados.length === 1 ? 'persona' : 'personas'}`}
                        chips={[
                            searchTerm && { key: 'q', label: `“${searchTerm}”`, onClear: () => setSearchTerm('') },
                            filtroEmpresa && { key: 'empresa', label: filtroEmpresa, onClear: () => setFiltroEmpresa('') },
                            filtroArea && { key: 'area', label: filtroArea, onClear: () => setFiltroArea('') },
                        ].filter(Boolean)}
                        onClearAll={() => { setSearchTerm(''); setFiltroEmpresa(''); setFiltroArea(''); }}
                    />
                </FilterDock>

                {empresasOrdenadas.length === 0 && (
                    <div style={studio.empty}>No hay personas con esos filtros.</div>
                )}

                {empresasOrdenadas.map((empresa) => (
                    <div key={empresa}>
                        <div style={studio.groupHead}>{empresa} · {usuariosPorEmpresa[empresa].length}</div>
                        <table style={studio.table}>
                            <thead>
                                <tr>
                                    <th style={{ ...studio.th, width: '52px' }}></th>
                                    <th style={studio.th}>Nombre</th>
                                    <th style={studio.th}>Email</th>
                                    <th style={studio.th}>Rol</th>
                                    <th style={studio.th}>Reportes</th>
                                    <th style={{ ...studio.th, width: '140px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {usuariosPorEmpresa[empresa].map((user) => (
                                    <tr
                                        key={user.uid}
                                        style={{
                                            ...(hoveredRow === user.uid ? studio.trHover : {}),
                                            cursor: 'pointer',
                                        }}
                                        onMouseEnter={() => setHoveredRow(user.uid)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                        onClick={() => setSelectedUserForPermissions(user)}
                                    >
                                        <td style={studio.td}>
                                            <img
                                                src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.nombre)}&background=5E35B1&color=fff&size=36`}
                                                alt=""
                                                style={studio.avatar}
                                            />
                                        </td>
                                        <td style={{ ...studio.td, fontWeight: 600 }}>{user.nombre}</td>
                                        <td style={{ ...studio.td, color: '#5f6368' }}>{userEmail(user)}</td>
                                        <td style={studio.td}><RoleBadge roleId={user.rol} /></td>
                                        <td style={studio.td}>
                                            <span style={{
                                                ...studio.tabBadge,
                                                ...(countsByUser[user.uid] ? studio.tabBadgeActive : {}),
                                            }}>
                                                {countsByUser[user.uid] || 0}
                                            </span>
                                        </td>
                                        <td style={studio.td}>
                                            <button
                                                type="button"
                                                style={{ ...studio.ghostBtn, padding: '7px 12px', fontSize: '0.82rem' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedUserForPermissions(user);
                                                }}
                                            >
                                                Gestionar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PermissionManagement;
